/**
 * ThemeBuilderEnhanced.tsx - Enhanced visual theme creation and editing interface
 * 
 * CHANGE HISTORY:
 * - 251122_2330 danielbeer: Complete rebuild with skin selection, sectioned settings, Templatr integration
 * 
 * Features:
 * - Load and edit existing skins from Skinnr
 * - Sectioned settings (Colors, Typography, Spacing, Layout, Borders, Shadows)
 * - Skin selector dropdown
 * - Close/Cancel buttons
 * - Templatr template integration
 * - Live preview
 */

import React, { useState, useEffect } from 'react';
import type { Theme } from '../features/ThemeInheritance';

export interface ThemeBuilderEnhancedProps {
  themeId?: string;
  skinnrContext?: any; // Skinnr context from useSkinnr hook
  onSave?: (theme: Theme) => void;
  onCancel?: () => void;
  onClose?: () => void;
}

type SectionTab = 'colors' | 'typography' | 'spacing' | 'layout' | 'borders' | 'shadows' | 'templates';

interface Skin {
  id: string;
  name: string;
  description?: string;
  variables: Record<string, string | number>;
}

export const ThemeBuilderEnhanced: React.FC<ThemeBuilderEnhancedProps> = ({
  themeId,
  skinnrContext,
  onSave,
  onCancel,
  onClose
}) => {
  const [activeSection, setActiveSection] = useState<SectionTab>('colors');
  const [selectedSkinId, setSelectedSkinId] = useState<string>(themeId || 'compact');
  const [availableSkins, setAvailableSkins] = useState<Skin[]>([]);
  const [currentSkin, setCurrentSkin] = useState<Skin | null>(null);
  
  // Settings state
  const [variables, setVariables] = useState<Record<string, string | number>>({});
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    // Load available skins from Skinnr context or use defaults
    let skins: Skin[] = [];
    
    if (skinnrContext && skinnrContext.getAllSkins) {
      skins = skinnrContext.getAllSkins();
    } else {
      // Use default skins from Skinnr package
      skins = [
        { id: 'compact', name: 'Compact (FileMaker Style)', description: 'Dense layout with minimal spacing', variables: {} },
        { id: 'grid-light', name: 'Grid Light Theme', description: 'Light theme for grid views', variables: {} },
        { id: 'grid-dark', name: 'Grid Dark Theme', description: 'Dark theme for grid views', variables: {} },
        { id: 'opcentral-dash', name: 'OpCentral Dashboard', description: 'Modern theme for OpCentral Dashboard', variables: {} }
      ];
    }
    
    setAvailableSkins(skins);

    // Load selected skin
    if (selectedSkinId) {
      let skin = skins.find((s: any) => s.id === selectedSkinId);
      
      // If skin not found in context, try to get it directly
      if (!skin && skinnrContext && skinnrContext.getSkin) {
        skin = skinnrContext.getSkin(selectedSkinId);
      }
      
      if (skin) {
        setCurrentSkin(skin);
        setVariables({ ...skin.variables });
      }
    }
  }, [selectedSkinId, skinnrContext]);

  const handleSkinChange = (skinId: string) => {
    setSelectedSkinId(skinId);
    setHasChanges(false);
  };

  const handleVariableChange = (key: string, value: string | number) => {
    setVariables(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const handleSave = () => {
    if (!currentSkin) return;

    const theme: Theme = {
      id: currentSkin.id,
      name: currentSkin.name,
      variables
    };

    if (onSave) {
      onSave(theme);
    }
    setHasChanges(false);
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else if (onClose) {
      onClose();
    }
  };

  const handlePreview = () => {
    // Apply variables to document root for live preview
    const root = document.documentElement;
    Object.entries(variables).forEach(([key, value]) => {
      root.style.setProperty(`--${key}`, String(value));
    });
  };

  const renderColorSection = () => {
    const colorVars = Object.entries(variables).filter(([key]) => 
      key.includes('color') || key.includes('bg') || key.includes('text')
    );

    return (
      <div className="section-content">
        <h3>Colors</h3>
        <div className="settings-grid">
          {colorVars.map(([key, value]) => (
            <div key={key} className="setting-row">
              <label>{key.replace(/-/g, ' ').replace(/^color /, '')}</label>
              <div className="color-input-group">
                <input
                  type="color"
                  value={String(value)}
                  onChange={(e) => handleVariableChange(key, e.target.value)}
                />
                <input
                  type="text"
                  value={String(value)}
                  onChange={(e) => handleVariableChange(key, e.target.value)}
                  style={{ fontFamily: 'monospace', fontSize: '12px' }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderTypographySection = () => {
    const typoVars = Object.entries(variables).filter(([key]) => 
      key.includes('font') || key.includes('line-height')
    );

    return (
      <div className="section-content">
        <h3>Typography</h3>
        <div className="settings-grid">
          {typoVars.map(([key, value]) => (
            <div key={key} className="setting-row">
              <label>{key.replace(/-/g, ' ')}</label>
              <input
                type="text"
                value={String(value)}
                onChange={(e) => handleVariableChange(key, e.target.value)}
              />
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderSpacingSection = () => {
    const spacingVars = Object.entries(variables).filter(([key]) => 
      key.includes('spacing') || key.includes('padding') || key.includes('gap')
    );

    return (
      <div className="section-content">
        <h3>Spacing & Padding</h3>
        <div className="settings-grid">
          {spacingVars.map(([key, value]) => (
            <div key={key} className="setting-row">
              <label>{key.replace(/-/g, ' ')}</label>
              <input
                type="text"
                value={String(value)}
                onChange={(e) => handleVariableChange(key, e.target.value)}
                placeholder="e.g., 4px, 0.5rem"
              />
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderLayoutSection = () => {
    const layoutVars = Object.entries(variables).filter(([key]) => 
      key.includes('row-height') || key.includes('label-size') || 
      key.includes('field-size') || key.includes('header-height')
    );

    return (
      <div className="section-content">
        <h3>Layout Dimensions</h3>
        <div className="settings-grid">
          {layoutVars.map(([key, value]) => (
            <div key={key} className="setting-row">
              <label>{key.replace(/-/g, ' ')}</label>
              <input
                type="text"
                value={String(value)}
                onChange={(e) => handleVariableChange(key, e.target.value)}
                placeholder="e.g., 20px, 1.5rem"
              />
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderBordersSection = () => {
    const borderVars = Object.entries(variables).filter(([key]) => 
      key.includes('border-radius') || key.includes('border')
    );

    return (
      <div className="section-content">
        <h3>Borders & Radius</h3>
        <div className="settings-grid">
          {borderVars.map(([key, value]) => (
            <div key={key} className="setting-row">
              <label>{key.replace(/-/g, ' ')}</label>
              <input
                type="text"
                value={String(value)}
                onChange={(e) => handleVariableChange(key, e.target.value)}
                placeholder="e.g., 4px, 0.25rem"
              />
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderShadowsSection = () => {
    const shadowVars = Object.entries(variables).filter(([key]) => 
      key.includes('shadow')
    );

    return (
      <div className="section-content">
        <h3>Shadows</h3>
        <div className="settings-grid">
          {shadowVars.map(([key, value]) => (
            <div key={key} className="setting-row">
              <label>{key.replace(/-/g, ' ')}</label>
              <input
                type="text"
                value={String(value)}
                onChange={(e) => handleVariableChange(key, e.target.value)}
                placeholder="e.g., 0 2px 4px rgba(0,0,0,0.1)"
                style={{ fontFamily: 'monospace', fontSize: '11px' }}
              />
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderTemplatesSection = () => {
    return (
      <div className="section-content">
        <h3>Templatr Templates</h3>
        <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '16px' }}>
          Load pre-configured theme templates from Templatr
        </p>
        <div className="template-list">
          <button className="template-button">
            📄 FileMaker Classic
            <span>Traditional FileMaker layout styling</span>
          </button>
          <button className="template-button">
            🎨 Modern Minimal
            <span>Clean, modern interface with generous spacing</span>
          </button>
          <button className="template-button">
            🌙 Dark Professional
            <span>Professional dark theme for extended use</span>
          </button>
          <button className="template-button">
            💼 Enterprise
            <span>Corporate-friendly color scheme</span>
          </button>
        </div>
      </div>
    );
  };

  const renderSection = () => {
    switch (activeSection) {
      case 'colors': return renderColorSection();
      case 'typography': return renderTypographySection();
      case 'spacing': return renderSpacingSection();
      case 'layout': return renderLayoutSection();
      case 'borders': return renderBordersSection();
      case 'shadows': return renderShadowsSection();
      case 'templates': return renderTemplatesSection();
      default: return null;
    }
  };

  return (
    <div className="theme-builder-enhanced">
      {/* Header */}
      <div className="theme-builder-header">
        <div className="header-left">
          <h2>🎨 Theme Builder</h2>
          <select 
            value={selectedSkinId} 
            onChange={(e) => handleSkinChange(e.target.value)}
            className="skin-selector"
          >
            <option value="">Select a skin to edit...</option>
            {availableSkins.map(skin => (
              <option key={skin.id} value={skin.id}>
                {skin.name}
              </option>
            ))}
          </select>
        </div>
        <div className="header-right">
          <button onClick={handleCancel} className="btn-secondary">
            ✕ Close
          </button>
        </div>
      </div>

      {/* Section Tabs */}
      <div className="section-tabs">
        <button 
          className={activeSection === 'colors' ? 'active' : ''}
          onClick={() => setActiveSection('colors')}
        >
          🎨 Colors
        </button>
        <button 
          className={activeSection === 'typography' ? 'active' : ''}
          onClick={() => setActiveSection('typography')}
        >
          📝 Typography
        </button>
        <button 
          className={activeSection === 'spacing' ? 'active' : ''}
          onClick={() => setActiveSection('spacing')}
        >
          📏 Spacing
        </button>
        <button 
          className={activeSection === 'layout' ? 'active' : ''}
          onClick={() => setActiveSection('layout')}
        >
          📐 Layout
        </button>
        <button 
          className={activeSection === 'borders' ? 'active' : ''}
          onClick={() => setActiveSection('borders')}
        >
          ⬜ Borders
        </button>
        <button 
          className={activeSection === 'shadows' ? 'active' : ''}
          onClick={() => setActiveSection('shadows')}
        >
          🌑 Shadows
        </button>
        <button 
          className={activeSection === 'templates' ? 'active' : ''}
          onClick={() => setActiveSection('templates')}
        >
          📄 Templates
        </button>
      </div>

      {/* Content */}
      <div className="theme-builder-content">
        {currentSkin ? (
          <>
            <div className="skin-info">
              <h4>{currentSkin.name}</h4>
              {currentSkin.description && <p>{currentSkin.description}</p>}
            </div>
            {renderSection()}
          </>
        ) : (
          <div className="no-skin-selected">
            <p>Select a skin from the dropdown to begin editing</p>
          </div>
        )}
      </div>

      {/* Footer Actions */}
      <div className="theme-builder-footer">
        <div className="footer-left">
          {hasChanges && (
            <span className="changes-indicator">● Unsaved changes</span>
          )}
        </div>
        <div className="footer-right">
          <button onClick={handlePreview} className="btn-secondary">
            👁️ Preview
          </button>
          <button onClick={handleCancel} className="btn-secondary">
            Cancel
          </button>
          <button 
            onClick={handleSave} 
            className="btn-primary"
            disabled={!hasChanges}
          >
            💾 Save Theme
          </button>
        </div>
      </div>

      <style>{`
        .theme-builder-enhanced {
          display: flex;
          flex-direction: column;
          height: 100%;
          background: var(--bg-primary, #ffffff);
          font-family: system-ui, -apple-system, sans-serif;
        }

        .theme-builder-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 24px;
          border-bottom: 1px solid var(--border-color, #e5e7eb);
          background: var(--bg-surface, #f9fafb);
        }

        .header-left {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .header-left h2 {
          margin: 0;
          font-size: 20px;
          font-weight: 600;
        }

        .skin-selector {
          padding: 8px 12px;
          border: 1px solid var(--border-color, #e5e7eb);
          border-radius: 6px;
          font-size: 14px;
          min-width: 250px;
        }

        .section-tabs {
          display: flex;
          gap: 4px;
          padding: 12px 24px;
          border-bottom: 1px solid var(--border-color, #e5e7eb);
          background: var(--bg-surface, #f9fafb);
          overflow-x: auto;
        }

        .section-tabs button {
          padding: 8px 16px;
          border: none;
          background: transparent;
          border-radius: 6px;
          font-size: 14px;
          cursor: pointer;
          white-space: nowrap;
          transition: all 0.2s;
        }

        .section-tabs button:hover {
          background: var(--bg-hover, rgba(0,0,0,0.05));
        }

        .section-tabs button.active {
          background: var(--color-primary, #2563eb);
          color: white;
        }

        .theme-builder-content {
          flex: 1;
          overflow-y: auto;
          padding: 24px;
        }

        .skin-info {
          margin-bottom: 24px;
          padding-bottom: 16px;
          border-bottom: 1px solid var(--border-color, #e5e7eb);
        }

        .skin-info h4 {
          margin: 0 0 8px 0;
          font-size: 18px;
        }

        .skin-info p {
          margin: 0;
          color: var(--text-secondary, #6b7280);
          font-size: 14px;
        }

        .section-content h3 {
          margin: 0 0 16px 0;
          font-size: 16px;
          font-weight: 600;
        }

        .settings-grid {
          display: grid;
          gap: 16px;
        }

        .setting-row {
          display: grid;
          grid-template-columns: 200px 1fr;
          align-items: center;
          gap: 12px;
        }

        .setting-row label {
          font-size: 14px;
          font-weight: 500;
          text-transform: capitalize;
        }

        .setting-row input[type="text"],
        .setting-row input[type="number"] {
          padding: 6px 10px;
          border: 1px solid var(--border-color, #e5e7eb);
          border-radius: 4px;
          font-size: 13px;
        }

        .color-input-group {
          display: flex;
          gap: 8px;
          align-items: center;
        }

        .color-input-group input[type="color"] {
          width: 50px;
          height: 36px;
          border: 1px solid var(--border-color, #e5e7eb);
          border-radius: 4px;
          cursor: pointer;
        }

        .color-input-group input[type="text"] {
          flex: 1;
        }

        .template-list {
          display: grid;
          gap: 12px;
        }

        .template-button {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          padding: 16px;
          border: 1px solid var(--border-color, #e5e7eb);
          border-radius: 8px;
          background: white;
          cursor: pointer;
          transition: all 0.2s;
          font-size: 14px;
          font-weight: 500;
        }

        .template-button:hover {
          border-color: var(--color-primary, #2563eb);
          background: var(--bg-hover, #f9fafb);
        }

        .template-button span {
          font-size: 12px;
          font-weight: 400;
          color: var(--text-secondary, #6b7280);
          margin-top: 4px;
        }

        .no-skin-selected {
          text-align: center;
          padding: 48px;
          color: var(--text-secondary, #6b7280);
        }

        .theme-builder-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 24px;
          border-top: 1px solid var(--border-color, #e5e7eb);
          background: var(--bg-surface, #f9fafb);
        }

        .footer-right {
          display: flex;
          gap: 12px;
        }

        .changes-indicator {
          color: var(--color-warning, #f59e0b);
          font-size: 14px;
          font-weight: 500;
        }

        .btn-primary {
          padding: 10px 20px;
          background: var(--color-primary, #2563eb);
          color: white;
          border: none;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-primary:hover:not(:disabled) {
          background: var(--color-primary-hover, #1d4ed8);
        }

        .btn-primary:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .btn-secondary {
          padding: 10px 20px;
          background: white;
          color: var(--text-primary, #1f2937);
          border: 1px solid var(--border-color, #e5e7eb);
          border-radius: 6px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-secondary:hover {
          background: var(--bg-hover, #f9fafb);
          border-color: var(--color-primary, #2563eb);
        }
      `}</style>
    </div>
  );
};

export default ThemeBuilderEnhanced;
